import { upperFirst } from 'lodash'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import { GraphCompareWithTotalResponse, GraphCompareResponse } from 'src/api/gen'
import { formatExportComparing } from 'src/commons/export/Export'
import BaseChartWrapper from 'src/commons/graphs/BaseChartWrapper'
import { DashboardGraphWrapper } from 'src/commons/graphs/GraphWrapper/GraphWrappers'
import { Can, userHasPermission } from 'src/commons/permissions'
import { IFilters, IGraphComponent } from 'src/commons/types'
import { createPayloadFromFilters } from 'src/commons/utils'
import WarnBanner from 'src/components/WarnBanner'
import { useProviders, useProviderUser } from 'src/layout/hooks'

import { scrollToComponent } from '../hooks'

import { EmissionsCompareToAlternateMode, EmissionsPerProvider, EnergyConsumptionPerProvider } from './charts'
import { formatExportEmissionsPerProvider, formatExportGazEmissionsCompareToAlternateVehicle } from './exports'

function ConsumptionPerProviderComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const isProviderUser = useProviderUser()
  const { activeProviders } = useProviders()

  async function fetcher(): Promise<GraphCompareResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    return await api.graphs.graphEnergyConsumptionPerProvider({ data })
  }

  function render(data: GraphCompareResponse[], title?: string) {
    return (
      <EnergyConsumptionPerProvider
        title={title}
        graphReport={data}
        granularity={props.filters.granularity}
        enableAnimation={false}
      />
    )
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = formatExportComparing(props.filters.granularity)
  }

  let title
  let description
  if (isProviderUser && activeProviders.length < 2) {
    title = upperFirst(t('kpi.EnergyConsumption.title'))
    description = upperFirst(t('kpi.EnergyConsumption.description'))
  } else {
    title = upperFirst(t('kpi.EnergyConsumptionPerProvider.title'))
    description = upperFirst(t('kpi.EnergyConsumptionPerProvider.description'))
  }

  return (
    <DashboardGraphWrapper
      updateOn={[props.filters]}
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
    />
  )
}

function GazEmissionsPerProvider(props: IGraphComponent) {
  const { t } = useTranslation()
  const isProviderUser = useProviderUser()
  const { activeProviders } = useProviders()

  async function fetcher(): Promise<GraphCompareWithTotalResponse> {
    const data = createPayloadFromFilters(props.filters)
    return api.graphs.graphGazEmissionPerProvider({ data })
  }

  function render(data: GraphCompareWithTotalResponse, title?: string) {
    return <EmissionsPerProvider title={title} graphReport={data} granularity={props.filters.granularity} />
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = formatExportEmissionsPerProvider(props.filters.granularity)
  }

  let title
  let description
  if (isProviderUser && activeProviders.length < 2) {
    title = upperFirst(t('kpi.Emissions.title'))
    description = upperFirst(t('kpi.Emissions.description'))
  } else {
    title = upperFirst(t('kpi.EmissionsPerProvider.title'))
    description = upperFirst(t('kpi.EmissionsPerProvider.description'))
  }

  return (
    <DashboardGraphWrapper
      updateOn={[props.filters]}
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
    />
  )
}

export function GazEmissionsCompareToAlternateVehicle(props: IGraphComponent) {
  const { t } = useTranslation()

  async function fetcher(): Promise<GraphCompareResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    return api.graphs.graphGazEmissionCompareToAlternateVehicle({ data })
  }

  function render(data: GraphCompareResponse[], title?: string) {
    return <EmissionsCompareToAlternateMode title={title} graphReport={data} granularity={props.filters.granularity} />
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = formatExportGazEmissionsCompareToAlternateVehicle(props.filters.granularity)
  }

  const title = upperFirst(t('kpi.GazEmissionsCompareToAlternateVehicle.title'))
  const description = upperFirst(t('kpi.GazEmissionsCompareToAlternateVehicle.description'))

  return (
    <DashboardGraphWrapper
      updateOn={[props.filters]}
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
    />
  )
}

interface IEnvironmentChartsProps {
  filters: IFilters
}

function EnvironmentCharts(props: IEnvironmentChartsProps) {
  useEffect(scrollToComponent)

  return (
    <BaseChartWrapper>
      <WarnBanner toDate={props.filters.range.to} />
      <Can I="read" an="trip">
        <>
          <ConsumptionPerProviderComponent filters={props.filters} />
          <GazEmissionsPerProvider filters={props.filters} />
          <GazEmissionsCompareToAlternateVehicle filters={props.filters} />
        </>
      </Can>
    </BaseChartWrapper>
  )
}

export default EnvironmentCharts
